'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Clock3, Search } from 'lucide-react';

import { listTimezones } from '@/lib/api/timezones';
import { systemTimezone as systemTz } from '@/lib/display-tz';
import { useDisplayTzStore } from '@/lib/display-tz/store';
import { cn } from '@/lib/util/cn';

const MAX_RENDER = 100; // cap rendered rows (599 total) — search narrows the rest

/** Short zone label for the chosen tz at `now` (e.g. "IST", "EST", "GMT+5:30"). */
function shortName(tz: string, now: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(now);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
  } catch {
    return tz;
  }
}

function clockAt(tz: string, now: Date): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: tz,
      hour12: false,
    }).format(now);
  } catch {
    return '—';
  }
}

/** LIVE UTC offset (minutes) for a zone at `at` — DST-aware, not the static DB
 * value. Reliable across engines via the wall-clock difference trick. */
function offsetMinutes(tz: string, at: Date): number {
  try {
    const inZone = new Date(at.toLocaleString('en-US', { timeZone: tz }));
    const inUtc = new Date(at.toLocaleString('en-US', { timeZone: 'UTC' }));
    return Math.round((inZone.getTime() - inUtc.getTime()) / 60_000);
  } catch {
    return 0;
  }
}

function fmtOffset(min: number): string {
  const sign = min < 0 ? '-' : '+';
  const a = Math.abs(min);
  const hh = String(Math.floor(a / 60)).padStart(2, '0');
  const mm = String(a % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
}

interface ZoneRow {
  id: string;
  offsetMin: number;
  offsetLabel: string;
}

export function TimezonePicker() {
  // The GLOBAL display tz lives in the shared store (persisted) — so picking here
  // re-renders every timestamp everywhere, not just this clock. null = system.
  const selected = useDisplayTzStore((s) => s.selected);
  const setSelected = useDisplayTzStore((s) => s.setSelected);
  const [hydrated, setHydrated] = useState(false);
  const [now, setNow] = useState<Date | null>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  // SSR-safe: the store rehydrates from localStorage on the client.
  useEffect(() => {
    setHydrated(true);
  }, []);

  // 1s clock tick.
  useEffect(() => {
    const tick = () => setNow(new Date());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // Close on outside-click / Escape.
  useEffect(() => {
    if (!open) {return;}
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {setOpen(false);}
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {setOpen(false);}
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Fetch the canonical list from the DB — only once the picker is opened.
  const tzQuery = useQuery({
    queryKey: ['timezones'],
    queryFn: ({ signal }) => listTimezones(signal),
    enabled: open,
    staleTime: 60 * 60 * 1000, // timezones change ~never
  });
  const allZones = useMemo(() => tzQuery.data?.data ?? [], [tzQuery.data]);

  const sysZone = hydrated ? systemTz() : 'UTC';
  const effectiveTz = selected ?? sysZone;
  const followingSystem = selected === null;

  const clock = now ? clockAt(effectiveTz, now) : '—';
  const label = hydrated && now ? shortName(effectiveTz, now) : '—';

  // Decorate every zone with its LIVE (DST-aware) offset, re-sorted by it. Memoed
  // on the list reference (react-query keeps it stable until refetch), so the
  // per-zone Intl work runs once per load, not on every keystroke.
  const decorated = useMemo<ZoneRow[]>(() => {
    const at = new Date();
    return allZones
      .map((t) => {
        const offsetMin = offsetMinutes(t.id, at);
        return { id: t.id, offsetMin, offsetLabel: fmtOffset(offsetMin) };
      })
      .sort((a, b) => a.offsetMin - b.offsetMin || a.id.localeCompare(b.id));
  }, [allZones]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = q
      ? decorated.filter(
          (t) =>
            t.id.toLowerCase().includes(q) || t.offsetLabel.toLowerCase().includes(q),
        )
      : decorated;
    return base.slice(0, MAX_RENDER);
  }, [decorated, filter]);

  const choose = (id: string | null) => {
    setSelected(id);       // store persists; drives the whole app's time rendering
    setOpen(false);
    setFilter('');
  };

  return (
    <div ref={rootRef} className="relative hidden md:block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`${effectiveTz}${followingSystem ? ' · following system' : ''}`}
        aria-label="Change display timezone"
        className={cn(
          'group inline-flex h-8 items-center gap-1.5 rounded-sm px-2',
          'border border-transparent text-fg-subtle',
          'hover:border-border hover:bg-surface hover:text-fg-muted',
          'transition-colors duration-fast',
          open && 'border-border bg-surface text-fg-muted',
        )}
      >
        <Clock3 className="h-3 w-3 shrink-0 stroke-[1.5]" aria-hidden />
        <span className="text-xs text-fg-subtle">{label}</span>
        <span className="text-xs tabular-nums text-fg-muted">{clock}</span>
        <ChevronDown className="h-3 w-3 shrink-0 stroke-[1.5] opacity-60" aria-hidden />
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 z-50 mt-1 w-72 overflow-hidden rounded-sm',
            'border border-border bg-bg-elev shadow-lg',
          )}
        >
          <div className="flex items-center gap-1.5 border-b border-divider px-2.5 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 stroke-[1.5] text-fg-subtle" aria-hidden />
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search timezone…"
              className="w-full bg-transparent text-xs text-fg outline-none placeholder:text-fg-subtle"
            />
          </div>

          <ul className="max-h-72 overflow-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => choose(null)}
                className={cn(
                  'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs',
                  'hover:bg-surface',
                  followingSystem ? 'text-accent' : 'text-fg-muted',
                )}
              >
                <span className="flex-1">
                  System default
                  <span className="ml-1.5 text-2xs text-fg-subtle">{sysZone}</span>
                </span>
                {followingSystem ? (
                  <Check className="h-3.5 w-3.5 shrink-0 stroke-[1.5]" aria-hidden />
                ) : null}
              </button>
            </li>

            <li className="my-1 h-px bg-divider" aria-hidden />

            {tzQuery.isLoading ? (
              <li className="px-2.5 py-2 text-2xs text-fg-subtle">Loading timezones…</li>
            ) : null}
            {tzQuery.isError ? (
              <li className="px-2.5 py-2 text-2xs text-crit">Failed to load timezones</li>
            ) : null}

            {filtered.map((t) => {
              const active = selected === t.id;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => choose(t.id)}
                    className={cn(
                      'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs',
                      'hover:bg-surface',
                      active ? 'text-accent' : 'text-fg-muted',
                    )}
                  >
                    <span className="w-[68px] shrink-0 text-2xs tabular-nums text-fg-subtle">
                      {t.offsetLabel}
                    </span>
                    <span className="flex-1 truncate">{t.id}</span>
                    {active ? (
                      <Check className="h-3.5 w-3.5 shrink-0 stroke-[1.5]" aria-hidden />
                    ) : null}
                  </button>
                </li>
              );
            })}

            {!tzQuery.isLoading && !tzQuery.isError && filtered.length === 0 ? (
              <li className="px-2.5 py-2 text-2xs text-fg-subtle">No match</li>
            ) : null}
          </ul>
        </div>
      )}
    </div>
  );
}
